import { CantileverButton } from 'components/CantileverButton'
import SearchBar from 'components/SearchBar'
import { TableCheckbox } from 'components/TableCheckbox'
import {
  KeywordContainer,
  useKeywordManagement,
  useScatterplotContext,
  useSensorDataAvailability,
  useTagFiltering,
} from 'features/timeSeries'
import { ChevronDown, Loader2 } from 'lucide-react'
import React, { useCallback, useMemo, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { BaseSensorEntry } from 'types'

import { AllTagsTable } from './AllTagsTable'

const MIN_SEARCH_LENGTH = 3

const AllTagsContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  min-height: 0;
  height: 100%;
`

const SearchContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem;
  flex-shrink: 0;
`

const SearchFieldStack = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 0.375rem;
  min-width: 0;
`

const spinnerRotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

const CheckboxControl = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  cursor: pointer;
  font-size: 0.75rem;
  line-height: 1rem;
  user-select: none;
`

const AvailabilityStatus = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: ${({ theme }) => theme.forecast.tertiaryText};
`

const CheckingIcon = styled(Loader2)`
  animation: ${spinnerRotate} 0.7s linear infinite;
  height: 0.75rem;
  width: 0.75rem;
`

const KeywordRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  padding: 0 0.5rem 0.5rem 0.5rem;
  width: 100%;
  flex-shrink: 0;
`

const ControlsRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
  padding: 0 0.5rem 0.5rem 0.5rem;
  width: 100%;
  flex-shrink: 0;
`

const SourceControl = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.75rem;
`

const SourceSelectWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`

const SourceSelect = styled.select`
  appearance: none;
  height: 1.75rem;
  padding: 0 1.5rem 0 0.5rem;
  border: none;
  border-radius: 0.25rem;
  background-color: ${({ theme }) => theme.forecast.defaultButton};
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.hoverButton};
  }

  &:focus-visible {
    outline: 1px solid ${({ theme }) => theme.forecast.activeText};
    outline-offset: -1px;
  }
`

const SelectChevron = styled(ChevronDown)`
  position: absolute;
  right: 0.375rem;
  height: 0.875rem;
  width: 0.875rem;
  pointer-events: none;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const ALL_SOURCES = '__all__'

const FilterModeButton = styled(CantileverButton)`
  max-width: 3rem;
`

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`

interface AllTagsTabProps {
  tags: BaseSensorEntry[]
  selectedTags: BaseSensorEntry[]
  hideFilterMode?: boolean
}

export const AllTagsTab: React.FC<AllTagsTabProps> = ({
  tags,
  selectedTags,
  hideFilterMode,
}) => {
  const { setSelectedSensors } = useScatterplotContext()
  const [hideTagsWithoutRecentData, setHideTagsWithoutRecentData] =
    useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [selectedSource, setSelectedSource] = useState<string>(ALL_SOURCES)

  const sourceOptions = useMemo(() => {
    const sources = new Set<string>()
    tags.forEach((tag) => {
      const pointSource = tag.data.point_source
      if (pointSource) {
        sources.add(pointSource)
      }
    })
    return Array.from(sources).sort((a, b) => a.localeCompare(b))
  }, [tags])

  const {
    tagNameSearch,
    setTagNameSearch,
    tagDescriptionSearch,
    setTagDescriptionSearch,
    tagNameKeywords,
    setTagNameKeywords,
    tagDescriptionKeywords,
    setTagDescriptionKeywords,
    filterMode,
    filteredTags,
    handleToggleFilterMode,
  } = useTagFiltering({ tags })
  const sourceFilteredTags = useMemo(
    () =>
      selectedSource === ALL_SOURCES
        ? filteredTags
        : filteredTags.filter(
            (tag) => tag.data.point_source === selectedSource
          ),
    [filteredTags, selectedSource]
  )
  const hasMeaningfulSearch =
    tagNameSearch.trim().length >= MIN_SEARCH_LENGTH ||
    tagDescriptionSearch.trim().length >= MIN_SEARCH_LENGTH ||
    tagNameKeywords.length > 0 ||
    tagDescriptionKeywords.length > 0
  const availabilitySurveyTagIds = useMemo(
    () =>
      hideTagsWithoutRecentData && hasMeaningfulSearch
        ? sourceFilteredTags.map((tag) => tag.id)
        : [],
    [sourceFilteredTags, hideTagsWithoutRecentData, hasMeaningfulSearch]
  )
  const { hasRecentDataBySensorId, isLoading: isCheckingTagAvailability } =
    useSensorDataAvailability(availabilitySurveyTagIds)
  const displayedTags = useMemo(
    () =>
      hideTagsWithoutRecentData
        ? sourceFilteredTags.filter(
            (tag) => hasRecentDataBySensorId[tag.id] !== false
          )
        : sourceFilteredTags,
    [sourceFilteredTags, hasRecentDataBySensorId, hideTagsWithoutRecentData]
  )

  const {
    handleRemoveTagNameKeyword,
    handleRemoveTagDescriptionKeyword,
    handleTagNameKeyDown,
    handleTagDescriptionKeyDown,
  } = useKeywordManagement({
    tagNameSearch,
    tagDescriptionSearch,
    tagNameKeywords,
    tagDescriptionKeywords,
    setTagNameKeywords,
    setTagDescriptionKeywords,
    setTagNameSearch,
    setTagDescriptionSearch,
  })

  const handleToggleTag = useCallback(
    (tag: BaseSensorEntry) => {
      setSelectedSensors((prev) => {
        const isSelected = prev.some((t) => t.id === tag.id)
        if (isSelected) {
          return prev.filter((t) => t.id !== tag.id)
        } else {
          return [...prev, tag]
        }
      })
    },
    [setSelectedSensors]
  )

  return (
    <AllTagsContainer>
      <SearchContainer>
        <SearchFieldStack>
          <SearchBar
            placeholder='Search by tag name...'
            value={tagNameSearch}
            onChange={(e) => setTagNameSearch(e.target.value)}
            onKeyDown={handleTagNameKeyDown}
            style={{ width: '100%' }}
          />
        </SearchFieldStack>
        {!hideFilterMode && (
          <FilterModeButton onClick={handleToggleFilterMode}>
            {filterMode}
          </FilterModeButton>
        )}
        <SearchFieldStack>
          <SearchBar
            placeholder='Search by description...'
            value={tagDescriptionSearch}
            onChange={(e) => setTagDescriptionSearch(e.target.value)}
            onKeyDown={handleTagDescriptionKeyDown}
            style={{ width: '100%' }}
          />
        </SearchFieldStack>
      </SearchContainer>
      {(tagNameKeywords.length > 0 || tagDescriptionKeywords.length > 0) && (
        <KeywordRow>
          <KeywordContainer
            keywords={tagNameKeywords}
            onRemoveKeyword={handleRemoveTagNameKeyword}
          />
          <KeywordContainer
            keywords={tagDescriptionKeywords}
            onRemoveKeyword={handleRemoveTagDescriptionKeyword}
          />
        </KeywordRow>
      )}
      <ControlsRow>
        {sourceOptions.length > 0 && (
          <SourceControl>
            <span>Source</span>
            <SourceSelectWrapper>
              <SourceSelect
                aria-label='Filter by source'
                value={selectedSource}
                onChange={(event) => setSelectedSource(event.target.value)}>
                <option value={ALL_SOURCES}>All sources</option>
                {sourceOptions.map((source) => (
                  <option key={source} value={source}>
                    {source}
                  </option>
                ))}
              </SourceSelect>
              <SelectChevron aria-hidden='true' />
            </SourceSelectWrapper>
          </SourceControl>
        )}
        <CheckboxControl>
          <TableCheckbox
            checked={hideTagsWithoutRecentData}
            onChange={(event) =>
              setHideTagsWithoutRecentData(event.target.checked)
            }
          />
          <span>Hide tags without data</span>
          {isCheckingTagAvailability && (
            <AvailabilityStatus aria-live='polite'>
              <CheckingIcon aria-hidden='true' />
              <span>Checking...</span>
            </AvailabilityStatus>
          )}
        </CheckboxControl>
        <CheckboxControl>
          <TableCheckbox
            checked={showDetails}
            onChange={(event) => setShowDetails(event.target.checked)}
          />
          <span>Show details</span>
        </CheckboxControl>
      </ControlsRow>
      <TableWrapper>
        <AllTagsTable
          filteredTags={displayedTags}
          hasRecentDataBySensorId={
            hideTagsWithoutRecentData ? hasRecentDataBySensorId : undefined
          }
          enableAvailabilitySurvey={hasMeaningfulSearch}
          selectedTags={selectedTags}
          onToggleTag={handleToggleTag}
          showDetails={showDetails}
        />
      </TableWrapper>
    </AllTagsContainer>
  )
}
